import { Injectable, Signal, WritableSignal, computed, inject, signal } from '@angular/core';

import { LoggerService } from '../../core/logging';
import { LocalStorageService } from './local-storage.service';

const LIGHT_THEME = 'ticket_app_light';
const DARK_THEME = 'ticket_app_dark';
const STORAGE_KEY = 'theme';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  public readonly currentTheme: Signal<string>;
  public readonly isDarkMode: Signal<boolean>;

  private readonly storageService = inject(LocalStorageService);
  private readonly logger = inject(LoggerService);
  private readonly themeSignal: WritableSignal<string>;

  constructor() {
    this.applyTheme(LIGHT_THEME);
    this.themeSignal = signal(this.readThemeOrDefault());
    this.currentTheme = this.themeSignal.asReadonly();
    this.isDarkMode = computed(() => this.themeSignal() === DARK_THEME);
    this.applyTheme(this.themeSignal());
    this.followSystemSchemeWhileUnset();
  }

  public getTheme(): string {
    return this.themeSignal();
  }

  public setTheme(theme: string): void {
    this.themeSignal.set(theme);
    this.storageService.setItem(STORAGE_KEY, theme);
    this.applyTheme(theme);
  }

  public toggleTheme(): void {
    this.setTheme(this.isDarkMode() ? LIGHT_THEME : DARK_THEME);
  }

  private applyTheme(theme: string): void {
    document.documentElement.setAttribute('data-theme', theme);
    document.body.setAttribute('data-theme', theme);
  }

  private readThemeOrDefault(): string {
    try {
      return this.readTheme();
    } catch (error: unknown) {
      this.logger.warn('Reading the stored theme failed, staying on light', error, {
        module: 'ThemeService',
      });

      return LIGHT_THEME;
    }
  }

  private readTheme(): string {
    const saved = this.readStoredTheme();

    if (saved) {
      return saved;
    }

    return this.systemSchemeQuery()?.matches ? DARK_THEME : LIGHT_THEME;
  }

  private readStoredTheme(): string | null {
    const saved = this.storageService.getItem<string>(STORAGE_KEY);

    return saved === LIGHT_THEME || saved === DARK_THEME ? saved : null;
  }

  private systemSchemeQuery(): MediaQueryList | null {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return null;
    }

    return window.matchMedia('(prefers-color-scheme: dark)');
  }

  private followSystemSchemeWhileUnset(): void {
    const query = this.systemSchemeQuery();

    if (!query) {
      return;
    }

    query.addEventListener('change', (event: MediaQueryListEvent): void => {
      if (this.readStoredTheme()) {
        return;
      }

      const theme = event.matches ? DARK_THEME : LIGHT_THEME;

      this.themeSignal.set(theme);
      this.applyTheme(theme);
    });
  }
}
